import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { motion } from "framer-motion";
import { Loader2, Smartphone, Mail, ShieldCheck, ArrowLeft, QrCode, RefreshCw } from "lucide-react";

export function MfaChallenge({ challenge, onComplete, onBack }) {
  const token = challenge.mfa_token;
  const [step, setStep] = useState(challenge.enrolled ? "verify" : "choose");
  const [method, setMethod] = useState(challenge.method || null);
  const [totp, setTotp] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleErr = (e) => {
    const msg = formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong";
    setError(msg);
    if (e.response?.status === 401) setTimeout(onBack, 1600);
  };

  const chooseEmail = async () => {
    setBusy(true); setError("");
    try {
      await api.post("/auth/mfa/setup/email", { mfa_token: token });
      setMethod("email"); setStep("enroll");
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const chooseTotp = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/mfa/setup/totp", { mfa_token: token });
      setTotp(data); setMethod("totp"); setStep("enroll");
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const url = step === "enroll" ? "/auth/mfa/enroll-verify" : "/auth/mfa/verify";
      const body = step === "enroll" ? { mfa_token: token, method, code } : { mfa_token: token, code };
      const { data } = await api.post(url, body);
      onComplete(data);
    } catch (e2) { handleErr(e2); } finally { setBusy(false); }
  };

  const resend = async () => {
    setError("");
    try { await api.post("/auth/mfa/resend", { mfa_token: token }); } catch (e) { handleErr(e); }
  };

  return (
    <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} data-testid="mfa-challenge">
      <button type="button" onClick={onBack} data-testid="mfa-back" className="mb-3 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-3 w-3" /> Back
      </button>
      <p className="mb-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-900">
        <ShieldCheck className="h-3.5 w-3.5 text-orange-600" /> Two-Step Verification
      </p>

      {step === "choose" && (
        <>
          <h1 className="font-heading text-xl font-extrabold uppercase tracking-tight text-slate-900">Secure your account</h1>
          <p className="mt-1 text-xs text-slate-700">Two-factor authentication is required. Choose how you'd like to verify each sign-in.</p>
          <div className="mt-4 space-y-2.5">
            <button type="button" onClick={chooseEmail} disabled={busy} data-testid="mfa-choose-email" className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors hover:border-orange-400 hover:bg-orange-50">
              <Mail className="h-5 w-5 text-blue-500" />
              <div><p className="font-semibold text-slate-900">Email code</p><p className="text-xs text-slate-600">We email a 6-digit code to {challenge.email}</p></div>
            </button>
            <button type="button" onClick={chooseTotp} disabled={busy} data-testid="mfa-choose-totp" className="flex w-full items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left transition-colors hover:border-orange-400 hover:bg-orange-50">
              <Smartphone className="h-5 w-5 text-violet-500" />
              <div><p className="font-semibold text-slate-900">Authenticator app</p><p className="text-xs text-slate-600">Use Google Authenticator, Authy, etc.</p></div>
            </button>
          </div>
          {busy && <Loader2 className="mx-auto mt-4 h-5 w-5 animate-spin text-orange-600" />}
        </>
      )}

      {(step === "enroll" || step === "verify") && (
        <form onSubmit={submit} className="mt-1">
          <h1 className="font-heading text-xl font-extrabold uppercase tracking-tight text-slate-900">
            {method === "totp" ? "Authenticator code" : "Check your email"}
          </h1>
          {method === "totp" && step === "enroll" && totp && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-center">
              <p className="mb-3 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-wider text-slate-600"><QrCode className="h-3.5 w-3.5" /> Scan this in your app</p>
              <img src={totp.qr} alt="2FA QR code" className="mx-auto h-40 w-40 rounded-md bg-white p-2" data-testid="mfa-qr-image" />
              <p className="mt-3 break-all font-mono text-[10px] text-slate-600">or enter key: {totp.secret}</p>
            </div>
          )}
          <p className="mt-2 text-xs text-slate-700">
            {method === "totp" ? "Enter the current 6-digit code from your authenticator app." : `Enter the 6-digit code we sent to ${challenge.email}.`}
          </p>
          <div className="mt-3">
            <Label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-600">Verification Code</Label>
            <Input inputMode="numeric" maxLength={6} data-testid="mfa-code-input" className="border-slate-300 bg-white text-center font-mono text-2xl tracking-[0.5em] text-slate-900" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" required />
          </div>
          {error && <p data-testid="mfa-error" className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <Button type="submit" disabled={busy || code.length < 6} data-testid="mfa-submit" className="mt-4 h-9 w-full bg-orange-500 text-white hover:bg-orange-600">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {step === "enroll" ? "Verify & Enable" : "Verify & Sign In"}
          </Button>
          {method === "email" && (
            <button type="button" onClick={resend} data-testid="mfa-resend" className="mt-3 w-full font-mono text-[11px] uppercase tracking-wider text-slate-900 hover:underline">Resend code</button>
          )}
          {step === "verify" && (
            <button type="button" onClick={() => { setStep("choose"); setError(""); setCode(""); setTotp(null); }} data-testid="mfa-switch-method" className="mt-3 flex w-full items-center justify-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-slate-700 hover:text-slate-900 hover:underline">
              <RefreshCw className="h-3 w-3" /> Didn't get a code? Use a different method
            </button>
          )}
        </form>
      )}
    </motion.div>
  );
}
